// Feedback moderation panel — admin-only. Rendered instead of the Chat
// pane when the admin toggles into moderation mode.
//
// UX:
//   - Filter tabs: Pending / Approved / Rejected / All
//   - Table of feedback entries with basic metadata
//   - Click a row to expand; shows the full question, answer preview,
//     tool-call summary, and moderator controls (approve / reject /
//     edit-then-approve).
//   - Export button generates a markdown digest of the selected filter
//     that the admin can copy-paste into a Claude Code session.

import { useEffect, useState } from 'react'
import {
  fetchAdminFeedback,
  patchAdminFeedback,
  exportAdminFeedback,
} from '../lib/api'
import type { AdminFeedbackRow, FeedbackStatus } from '../lib/types'

const TABS: { key: FeedbackStatus | 'all'; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
]

const CATEGORY_LABELS: Record<string, string> = {
  incorrect_data: 'Incorrect data',
  wrong_interpretation: 'Wrong interpretation',
  missing_context: 'Missing context',
  definition_ambiguity: 'Definition ambiguity',
  tool_behavior: 'Tool behavior',
  other: 'Other',
}

export function AdminFeedback() {
  const [tab, setTab] = useState<FeedbackStatus | 'all'>('pending')
  const [rows, setRows] = useState<AdminFeedbackRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [exportOpen, setExportOpen] = useState<'approved' | 'pending' | 'rejected' | null>(null)
  const [exportMd, setExportMd] = useState<string>('')
  const [exportCount, setExportCount] = useState(0)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const resp = await fetchAdminFeedback(tab === 'all' ? undefined : tab, 200)
      setRows(resp.feedback)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  async function moderate(row: AdminFeedbackRow, updates: {
    status?: FeedbackStatus
    admin_notes?: string
    what_was_wrong?: string
    suggested_answer?: string
    category?: string
  }) {
    try {
      await patchAdminFeedback(row.id, updates)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function openExport(status: FeedbackStatus) {
    setExportOpen(status)
    setExportMd('')
    try {
      const r = await exportAdminFeedback(status)
      setExportMd(r.markdown)
      setExportCount(r.count)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-3 border-b border-marker-border bg-marker-panel">
        <div>
          <div className="text-lg font-semibold text-marker-text">Feedback review</div>
          <div className="text-xs text-marker-muted">
            User-submitted feedback awaiting moderation. Approve or reject each item; use "Export" to build a digest for Claude Code.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void openExport('approved')}
            className="text-xs px-3 py-1.5 rounded border border-marker-accent text-marker-accent hover:bg-marker-accent hover:text-white"
            title="Export approved feedback as markdown for Claude Code"
          >
            Export approved
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 pt-3 border-b border-marker-border bg-marker-panel">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              'px-3 py-1.5 text-sm border-b-2 -mb-px ' +
              (tab === t.key
                ? 'border-marker-accent text-marker-text'
                : 'border-transparent text-marker-muted hover:text-marker-text')
            }
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-marker-muted">
          {loading ? 'Loading…' : `${rows.length} item${rows.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {error && (
          <div className="mb-3 p-2 text-sm text-marker-bad border border-marker-bad rounded">
            {error}
          </div>
        )}

        {!loading && rows.length === 0 && !error && (
          <div className="text-sm text-marker-muted p-4 text-center">
            No feedback in this state.
          </div>
        )}

        <div className="space-y-2">
          {rows.map((row) => (
            <FeedbackRowCard
              key={row.id}
              row={row}
              expanded={expandedId === row.id}
              onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
              onModerate={(updates) => moderate(row, updates)}
            />
          ))}
        </div>
      </div>

      {/* Export modal */}
      {exportOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setExportOpen(null)}
        >
          <div
            className="bg-marker-panel border border-marker-border rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-marker-border">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-marker-text">
                    Export: {exportOpen} feedback ({exportCount} item{exportCount === 1 ? '' : 's'})
                  </div>
                  <div className="text-xs text-marker-muted mt-1">
                    Copy this markdown and paste it into a Claude Code chat to convert into rule/definition updates.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(exportMd)
                    }}
                    className="text-xs px-3 py-1.5 rounded bg-marker-accent text-white hover:bg-marker-accent2"
                  >
                    Copy to clipboard
                  </button>
                  <button
                    onClick={() => setExportOpen(null)}
                    className="text-xs px-3 py-1.5 rounded border border-marker-border text-marker-muted hover:text-marker-text"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-marker-text whitespace-pre-wrap">
              {exportMd || '(loading…)'}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}


function FeedbackRowCard({
  row,
  expanded,
  onToggle,
  onModerate,
}: {
  row: AdminFeedbackRow
  expanded: boolean
  onToggle: () => void
  onModerate: (updates: {
    status?: FeedbackStatus
    admin_notes?: string
    what_was_wrong?: string
    suggested_answer?: string
    category?: string
  }) => void
}) {
  const [editMode, setEditMode] = useState(false)
  const [editWhatWasWrong, setEditWhatWasWrong] = useState(row.what_was_wrong || '')
  const [editSuggestedAnswer, setEditSuggestedAnswer] = useState(row.suggested_answer || '')
  const [editCategory, setEditCategory] = useState(row.category || '')
  const [editAdminNotes, setEditAdminNotes] = useState(row.admin_notes || '')

  const ratingColor = row.rating === 'good'
    ? 'text-marker-good'
    : row.rating === 'wrong'
    ? 'text-marker-bad'
    : 'text-marker-warn'

  const statusChip = (
    <span className={
      'inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ' +
      (row.status === 'pending'
        ? 'bg-marker-warn/20 text-marker-warn'
        : row.status === 'approved'
        ? 'bg-marker-good/20 text-marker-good'
        : 'bg-marker-bad/20 text-marker-bad')
    }>
      {row.status}
    </span>
  )

  return (
    <div className="border border-marker-border rounded-lg bg-marker-panel">
      {/* Row header */}
      <div
        onClick={onToggle}
        className="p-3 cursor-pointer hover:bg-marker-panel2 flex items-center gap-3"
      >
        <span className={ratingColor + ' text-lg'}>
          {row.rating === 'good' ? '👍' : row.rating === 'wrong' ? '👎' : '⚠'}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-marker-text truncate">
            {row.question || '(audit not found)'}
          </div>
          <div className="text-xs text-marker-muted mt-0.5 flex items-center gap-2">
            {statusChip}
            {row.category && (
              <span className="text-marker-muted">· {CATEGORY_LABELS[row.category] || row.category}</span>
            )}
            <span>· {row.user_email}</span>
            <span>· {formatDate(row.created_at)}</span>
          </div>
        </div>
        <span className="text-marker-muted text-sm">{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div className="border-t border-marker-border p-3 space-y-3 text-sm">
          {/* Full question */}
          <div>
            <div className="text-xs uppercase tracking-wide text-marker-muted mb-1">Question</div>
            <div className="text-marker-text whitespace-pre-wrap">
              {row.question || '(audit not found)'}
            </div>
          </div>

          {/* Answer preview */}
          <div>
            <div className="text-xs uppercase tracking-wide text-marker-muted mb-1">Answer given</div>
            <div className="text-marker-text whitespace-pre-wrap max-h-64 overflow-y-auto border border-marker-border rounded p-2 bg-marker-bg">
              {row.answer || '(no answer stored)'}
            </div>
          </div>

          {/* Original user report */}
          {(row.what_was_wrong || row.suggested_answer || row.comment) && (
            <div className="border border-marker-border rounded p-2 bg-marker-bg space-y-2">
              {row.what_was_wrong && (
                <div>
                  <div className="text-xs text-marker-muted">User said what was wrong:</div>
                  <div className="text-marker-text whitespace-pre-wrap">{row.what_was_wrong}</div>
                </div>
              )}
              {row.suggested_answer && (
                <div>
                  <div className="text-xs text-marker-muted">User's suggested correct answer:</div>
                  <div className="text-marker-text whitespace-pre-wrap">{row.suggested_answer}</div>
                </div>
              )}
              {row.comment && !row.what_was_wrong && (
                <div>
                  <div className="text-xs text-marker-muted">User's comment (legacy field):</div>
                  <div className="text-marker-text whitespace-pre-wrap">{row.comment}</div>
                </div>
              )}
            </div>
          )}

          {/* Reviewer notes / edit form */}
          <div className="border-t border-marker-border pt-3 space-y-2">
            <div className="text-xs uppercase tracking-wide text-marker-muted">Reviewer</div>

            {!editMode && (
              <>
                {row.admin_notes && (
                  <div className="text-marker-text whitespace-pre-wrap p-2 bg-marker-bg rounded border border-marker-border">
                    {row.admin_notes}
                  </div>
                )}
                {row.reviewed_by && (
                  <div className="text-xs text-marker-muted">
                    Reviewed by {row.reviewed_by} · {formatDate(row.reviewed_at || '')}
                  </div>
                )}
              </>
            )}

            {editMode && (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-marker-muted">Edited: what was wrong</label>
                  <textarea
                    value={editWhatWasWrong}
                    onChange={(e) => setEditWhatWasWrong(e.target.value)}
                    rows={2}
                    className="w-full text-sm bg-marker-bg border border-marker-border rounded p-2 text-marker-text focus:outline-none focus:border-marker-accent"
                  />
                </div>
                <div>
                  <label className="text-xs text-marker-muted">Edited: suggested correct answer</label>
                  <textarea
                    value={editSuggestedAnswer}
                    onChange={(e) => setEditSuggestedAnswer(e.target.value)}
                    rows={3}
                    className="w-full text-sm bg-marker-bg border border-marker-border rounded p-2 text-marker-text focus:outline-none focus:border-marker-accent"
                  />
                </div>
                <div>
                  <label className="text-xs text-marker-muted">Category</label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="w-full text-sm bg-marker-bg border border-marker-border rounded p-2 text-marker-text focus:outline-none focus:border-marker-accent"
                  >
                    <option value="">Uncategorized</option>
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-marker-muted">Admin notes (goes into export)</label>
                  <textarea
                    value={editAdminNotes}
                    onChange={(e) => setEditAdminNotes(e.target.value)}
                    rows={2}
                    className="w-full text-sm bg-marker-bg border border-marker-border rounded p-2 text-marker-text focus:outline-none focus:border-marker-accent"
                    placeholder="e.g., Add ISDR/OSDR distinction to the SDR-metrics definition"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-marker-border">
            {!editMode && row.status !== 'approved' && (
              <button
                onClick={() => onModerate({ status: 'approved' })}
                className="px-3 py-1.5 text-xs rounded bg-marker-good text-white hover:opacity-90"
                title="Approve as-is — user's report is accurate"
              >
                Approve
              </button>
            )}
            {!editMode && row.status !== 'rejected' && (
              <button
                onClick={() => onModerate({ status: 'rejected' })}
                className="px-3 py-1.5 text-xs rounded border border-marker-bad text-marker-bad hover:bg-marker-bad hover:text-white"
                title="Reject — not actionable or not a real issue"
              >
                Reject
              </button>
            )}
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="px-3 py-1.5 text-xs rounded border border-marker-border text-marker-text hover:border-marker-accent"
                title="Edit the user's fields + add notes before approving"
              >
                Edit & approve
              </button>
            )}
            {editMode && (
              <>
                <button
                  onClick={() => {
                    onModerate({
                      status: 'approved',
                      what_was_wrong: editWhatWasWrong,
                      suggested_answer: editSuggestedAnswer,
                      category: editCategory,
                      admin_notes: editAdminNotes,
                    })
                    setEditMode(false)
                  }}
                  className="px-3 py-1.5 text-xs rounded bg-marker-good text-white hover:opacity-90"
                >
                  Save & approve
                </button>
                <button
                  onClick={() => {
                    setEditMode(false)
                    setEditWhatWasWrong(row.what_was_wrong || '')
                    setEditSuggestedAnswer(row.suggested_answer || '')
                    setEditCategory(row.category || '')
                    setEditAdminNotes(row.admin_notes || '')
                  }}
                  className="px-3 py-1.5 text-xs text-marker-muted hover:text-marker-text"
                >
                  Cancel
                </button>
              </>
            )}
            {row.status !== 'pending' && !editMode && (
              <button
                onClick={() => onModerate({ status: 'pending' })}
                className="px-3 py-1.5 text-xs text-marker-muted hover:text-marker-text ml-auto"
                title="Move back to pending"
              >
                ← Return to pending
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


function formatDate(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}
